import { ListPrice, Phase } from 'modules/Cube/domain/cube.domain.types'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { toPriceSummary, toBillingFrequencyLabel } from '@sequencehq/utils'
import {
  CreateMenuItem,
  Option,
  OptionGroup
} from '@sequencehq/core-components'
import { IndigoIndigo60 } from '@sequencehq/core-components'
import { useLoadAllProductsAndListPrices } from 'Products/index'

type UseSearchForProduct = (props: {
  phaseId: Phase['id']
  onSelection?: (entityId: string) => void
}) => {
  productOptions: Option[]
  productOptionsWithListPrices: OptionGroup[]
  onSelection: (entityId: string) => void
  onAddNew: () => void
  showAllProductsAddedStateWhenEmpty: boolean
  isLoading: boolean
}

const generateListPriceOption = (listPrice: ListPrice) => {
  const priceSummary = `${toPriceSummary(listPrice)} / ${toBillingFrequencyLabel(
    listPrice.billingFrequency
  )}`

  // If list price has a name (display name), show it as primary label
  // with price summary as secondary label
  if (listPrice.name) {
    return {
      id: listPrice.id,
      label: priceSummary,
      secondaryLabel: listPrice.name,
      value: listPrice.id
    }
  }

  // Fallback to price summary as label if no name
  return {
    id: listPrice.id,
    label: priceSummary,
    value: listPrice.id
  }
}

export const useSearchForProduct: UseSearchForProduct = props => {
  const navigate = useNavigate()
  const cubeContext = useCubeContext()

  /**
   * We don't want to include existing products in the search, since it leads
   * to strange behaviour given that we're looking to 'edit' the product, like
   * jumping directly into the edit mode for the current price.
   */
  const existingProductsInPhase = useMemo(() => {
    return Object.values(
      cubeContext.queries.resolvedPhases[props.phaseId]?.prices
    ).map(productPrice => productPrice.productId)
  }, [cubeContext, props.phaseId])

  const { allProductsAndListPrices, allListPrices, isLoading } =
    useLoadAllProductsAndListPrices()

  const productOptions = useMemo(() => {
    return allProductsAndListPrices
      .filter(product => !existingProductsInPhase.includes(product.id))
      .map(product => ({
        label: product.name,
        value: product.id
      }))
      .sort((a, b) => (a.label < b.label ? -1 : 1))
  }, [allProductsAndListPrices, existingProductsInPhase])

  const productOptionsWithListPrices = useMemo(() => {
    return allProductsAndListPrices
      .map(product => {
        const listPriceOptions = product.listPrices
          /**
           * Ensure we only include list prices that match the selected currency
           * of the current pricing
           */
          .filter(
            lp =>
              !cubeContext.queries.selectedCurrency ||
              lp.currency === cubeContext.queries.selectedCurrency
          )
          .map(listPrice => generateListPriceOption(listPrice))

        return {
          id: product.id,
          key: product.id,
          label: product.name,
          value: product.id,
          options: listPriceOptions,
          emptyState: (
            <CreateMenuItem
              data-testid={`select.${product.id}.newPrice`}
              onClick={() => {
                navigate(`./price-editor/${props.phaseId}/${product.id}`)
              }}
              color={IndigoIndigo60}
            >
              New price variant
            </CreateMenuItem>
          )
        }
      })
      .filter(product => !existingProductsInPhase.includes(product.id))
      .sort((a, b) => (a.label < b.label ? -1 : 1))
  }, [
    existingProductsInPhase,
    allProductsAndListPrices,
    navigate,
    props.phaseId
  ])

  const onSelection = useCallback(
    (listPriceId: string) => {
      const listPrice = allListPrices.find(lp => lp.id === listPriceId)
      const productId = listPrice?.productId

      if (!productId) {
        // If we don't have a product ID, we can't proceed.
        console.warn(
          `No product found for listPriceId: ${listPriceId}. Cannot navigate to price editor.`
        )
        return
      }

      props.onSelection?.(listPriceId)
      return navigate(
        `./price-editor/${props.phaseId}/${productId}/${listPriceId}`
      )
    },
    [navigate, allListPrices, props.onSelection, props.phaseId]
  )

  /**
   * Mooch on over to the new product route in the products module. This will cause the user
   * to leave Cube, but in the future we should seek to better tutorialize and restrict the user
   * so that items like products/list prices are set up before starting a schedule.
   *
   * Then, allow for saving drafts of schedules with no products added!
   */
  const onAddNew = useCallback(() => {
    return navigate(`/products/new`)
  }, [navigate])

  return {
    productOptions,
    productOptionsWithListPrices,
    showAllProductsAddedStateWhenEmpty: existingProductsInPhase.length > 0,
    onSelection,
    onAddNew,
    isLoading
  }
}
